/**
 * External Imports
 */
import { differenceInHours, parse } from 'date-fns';

/**
 * Internal Imports
 */
import {
  date,
  date as dt,
  employeeHireDateHelper
} from '../../../utils/helpers';
import qualifyingEmpRoundingTemplates from '../../../constants/qualifyingEmpRoundingTemplates.json';

/**
 * Global Type Definition Imports
 */
import {
  EmpFreqReport,
  Employee,
  EmpRoundsReport,
  Patient,
  PatientRoundsReport,
  RoundHistory,
  Task
} from '../../../config/interfaces';

/**
 * Task Api Response
 */

// TODO: All of these need better typedefs between response contract and cleaned type
export const cleanIssue = (task: Task) => {
  let taskStatus;
  if (task.completed) {
    taskStatus = 'Completed';
  } else {
    taskStatus = 'Opened';
  }
  return {
    friendlyId: task.friendlyId,
    taskId: task.taskId,
    userId: task.userId,
    taskType: task.taskType,
    category: task.category,
    taskSubtype: task.taskSubtype,
    subCategory: task.subCategory,
    taskCategory: task.taskCategory,
    title: task.title,
    completed: task.completed,
    completedDate: task.completedDate,
    taskDate: task.taskDate,
    description: task.description,
    facilityId: task.facilityId,
    patientFirstName: task.patientFirstName,
    patientLastName: task.patientLastName,
    patientFullName: task.patientFullName,
    employeeFirstName: task.employeeFirstName,
    employeeLastName: task.employeeLastName,
    employeeFullName: task.employeeFullName,
    employeeId: (task.employeeId as string)?.toUpperCase(),
    room: task.room,
    bed: task.bed,
    roundingType: task.roundingType,
    sharedWith: task.sharedWith,
    spotlight: task.isCopiedToStoplight,
    taskStatus,
    updated: task.updated,
    updatedBy: task.updatedBy,
    unitId: task.unitId,
    userFullName: task.userFullName,
    stoplightId: task.stoplightId,
    ticketNumber: task.ticketNumber,
    latestComment: task.latestComment,
    discussion: task.discussion,
    discussionId: task.discussionId,
    department: task.department,
    departmentCode: task.departmentCode,
    departmentId: task.departmentId,
    callback: task.callback || task.u_best_contact_info,
    u_best_contact_info: task.u_best_contact_info,
    u_issue_location: task.u_issue_location,
    u_control_number: task.u_control_number,
    u_non_routine_justification: task.u_non_routine_justification,
    ec_loc: task.ec_loc,
    roundId: task.roundId,
    accountNum: task.accountNum
  };
};

export const cleanFeedback = (task: Task) => {
  let taskStatus;
  if (task.completed) {
    taskStatus = 'Completed';
  } else {
    taskStatus = 'Opened';
  }
  return {
    friendlyId: task.friendlyId,
    taskId: task.taskId,
    userId: task.userId,
    taskType: task.taskType,
    category: task.category,
    taskSubtype: task.taskSubtype,
    subCategory: task.subCategory,
    taskCategory: task.taskCategory,
    title: task.title,
    completed: task.completed,
    completedDate: task.completedDate,
    taskDate: task.taskDate,
    description: task.description,
    facilityId: task.facilityId,
    patientFirstName: task.patientFirstName,
    patientLastName: task.patientLastName,
    patientFullName: task.patientFullName,
    employeeFirstName: task.employeeFirstName,
    employeeLastName: task.employeeLastName,
    employeeFullName: task.employeeFullName,
    employeeId: (task.employeeId as string)?.toUpperCase(),
    room: task.room,
    bed: task.bed,
    roundingType: task.roundingType,
    sharedWith: task.sharedWith,
    spotlight: task.isCopiedToStoplight,
    taskStatus,
    updated: task.updated,
    updatedBy: task.updatedBy,
    unitId: task.unitId,
    userFullName: task.userFullName,
    stoplightId: task.stoplightId,
    ticketNumber: task.ticketNumber,
    latestComment: task.latestComment,
    discussion: task.discussion,
    discussionId: task.discussionId,
    department: task.department,
    departmentCode: task.departmentCode,
    departmentId: task.departmentId,
    callback: task.callback || task.u_best_contact_info,
    u_best_contact_info: task.u_best_contact_info,
    u_issue_location: task.u_issue_location,
    u_control_number: task.u_control_number,
    u_non_routine_justification: task.u_non_routine_justification,
    ec_loc: task.ec_loc,
    roundId: task.roundId,
    accountNum: task.accountNum
  };
};

/**
 * Users Api Response
 */

export const cleanPatientToRound = (patient: Patient) => {
  if (patient.admitTime) {
    const today = new Date();
    const admitTime = patient?.admitTime
      ? patient.admitTime.length === 12
        ? parse(patient?.admitTime, 'yyyyMMddHHmm', new Date())
        : patient.admitTime.length === 14
        ? parse(patient?.admitTime, 'yyyyMMddHHmmss', new Date())
        : undefined
      : undefined;
    const lastRoundDate =
      patient?.lastRound?.roundDate &&
      ((dt.isOrbitTimestamp(patient?.lastRound?.roundDate) &&
        `${dt.dayOfYr(patient.lastRound.roundDate)} @ ${dt.localTimeOfDay(
          patient.lastRound.roundDate
        )}`) ||
        'Unknown');

    let roundStatus;
    const lengthOfStay = admitTime ? differenceInHours(today, admitTime) : -1;
    if (patient.completed) {
      roundStatus = 'Complete';
    } else {
      roundStatus = 'To Do';
    }
    const fullName = `${patient.firstName} ${patient.lastName}`;

    return {
      ...patient,
      roundStatus,
      lastRoundDate: patient?.lastRound?.roundDate,
      lastRoundedBy: patient?.lastRound?.roundedBy,
      formattedLastRoundDate: lastRoundDate,
      lengthOfStay,
      fullName
    };
  }
  const fullName = `${patient.firstName} ${patient.lastName}`;

  return {
    ...patient,
    roundStatus: 'To Do',
    lastRoundDate: null,
    lastRoundedBy: null,
    formattedLastRoundDate: null,
    fullName
  };
};

export const cleanUnlistedPatientToRound = (history: RoundHistory) => {
  return {
    roundStatus:
      history.completed &&
      history?.roundDate &&
      dt.isOrbitTimestamp(history?.roundDate) &&
      dt.isToday(dt.parseDate(history?.roundDate))
        ? 'Complete'
        : 'To Do',
    accountNum: history.accountNum,
    mrn: history.patientMRN,
    urn: history.patientId,
    encounterId: '',
    unitId: history.unitId,
    dateOfBirth: '',
    firstName: history.patientFirstName?.toUpperCase(),
    lastName: history.patientLastName?.toUpperCase(),
    room: history.room,
    bed: history.bed,
    admitTime: '',
    dischargeTime: '',
    patientStatus: '',
    transferred: false,
    sex: '',
    chiefComplaint: '',
    attendingPhysician: '',
    notes: [],
    lastRoundId: history.roundId,
    lastRoundDate: history.roundDate,
    formattedLastRoundDate: `${dt.dayOfYr(
      history?.roundDate
    )} @ ${dt.localTimeOfDay(history?.roundDate)}`,
    lengthOfStay: -1,
    lastRoundedBy: `${history.userFirstName} ${history.userLastName}`
  };
};

export const cleanEmployeeToRound = (employee: Employee) => {
  return {
    ...employee,
    fullName: `${employee.firstName} ${employee.lastName}`,
    lastRoundDate: employee?.lastRound?.roundDate,
    lastRoundedBy: employee?.lastRound?.roundedBy || 'Unknown'
  };
};

export const buildSubject = (history: any) => ({
  ...history,
  adHoc: true,
  hcaid: history.employeeId,
  firstName: history.employeeFirstName,
  lastName: history.employeeLastName,
  fullName: `${history.employeeFirstName} ${history.employeeLastName}`,
  lastRoundDate: history?.roundDate,
  lastRoundedBy:
    `${history?.userFirstName} ${history?.userLastName}`.trim() || 'Unknown',
  lastRoundedById: history?.userId,
  lastRoundedByTitle: history?.userTitle,
  tag: history?.tags?.join('') ?? ''
});

/**
 * Admin Api Response
 */

export const cleanEmpDepartment = (dept: any) => ({
  departmentName: dept.name,
  departmentId: dept.id
});

/**
 * Reports Api Response
 */

export const cleanPatientRoundsReportDownload = (
  patientRoundsReportList: any
): PatientRoundsReport[] => {
  const cleanedPatientRoundsReportList =
    patientRoundsReportList.map((round: any): PatientRoundsReport => {
      const answersWithIndex =
        round.answers?.map((ans: any, index: any) => ({
          [`question${index + 1}`]: ans?.question?.replaceAll('"', ''),
          [`answer${index + 1}`]:
            ans?.answer && ans.answer.length > 0 ? ans?.answer[0].value : ''
        })) || [];
      const ansObject = answersWithIndex.reduce(
        (acc: any, value: any) => ({
          ...acc,
          ...value
        }),
        {}
      );
      return {
        unit: round?.unit || '',
        room: round?.room || '',
        patientId: round?.patientId || '',
        patient: `${round?.patientFirstName} ${round?.patientLastName}`,
        patientFirstName: round?.patientFirstName || '',
        patientLastName: round?.patientLastName || '',
        patientMRN: round?.patientMRN || '',
        createdUserName: `${round?.userFirstName} ${round?.userLastName}`,
        createdUserId: round?.userId || '',
        accountNumber: round?.accountNum || '',
        facilityId: round?.facilityId || '',
        status: round.status === 'rounded' ? 'Rounded' : 'Unable To Round',
        date:
          round?.roundDate && date.isOrbitTimestamp(round?.roundDate)
            ? date.parseDate(round?.roundDate).toLocaleString([], {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit'
              })
            : '',
        time:
          round?.roundDate && date.isOrbitTimestamp(round?.roundDate)
            ? date.parseDate(round?.roundDate).toLocaleString([], {
                hour: '2-digit',
                minute: '2-digit'
              })
            : '',
        templateName: round?.templateName || '',
        ...ansObject
      };
    }) || [];
  return cleanedPatientRoundsReportList;
};

export const cleanEmployeeRoundsReportDownload = (
  employeeRoundsReportList: any,
  arg: any
): EmpRoundsReport[] => {
  const mostRecentRoundCleanData =
    employeeRoundsReportList
      ?.filter(
        (round: any) =>
          qualifyingEmpRoundingTemplates.includes(round.templateName) ||
          round?.complianceStatus === 'notrounded'
      )
      .reduce((acc: EmpRoundsReport[], round: any) => {
        return [
          ...acc,
          {
            roundId: round?.roundId || '',
            department: round?.employeeDept || 'Unknown',
            employee:
              `${round?.employeeFirstName} ${round?.employeeLastName}` || '',
            employeeId: round?.employeeId || '',
            employeeFirstName: round?.employeeFirstName || '',
            employeeLastName: round?.employeeLastName || '',
            employeeHireDate: employeeHireDateHelper(round?.employeeHireDate)
              ? date.datePrettier(round?.employeeHireDate)
              : 'Unknown',
            roundedBy:
              round?.userFirstName && round?.userLastName
                ? `${round?.userFirstName} ${round?.userLastName}`
                : '',
            createdUserId: round?.userId || '',
            facilityId: round?.facilityId || '',
            date: round?.roundDate || '',
            roundDate: round?.roundDate ? date.dayOfYr(round?.roundDate) : '',
            status:
              round?.complianceStatus === 'compliant'
                ? 'Compliant'
                : round?.complianceStatus === 'notrounded'
                ? 'Not Rounded'
                : 'Rounded',
            templateName:
              round?.templateName === 'Unknown'
                ? ''
                : round?.templateName || '',
            answers: round?.answers || []
          }
        ];
      }, []) || [];

  // TODO: Front-end has to handle sorting until mobile no longer needs export endpoint
  if (arg.sortBy === 'employeeDept') {
    if (arg.sortOrder === 'asc') {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        a.department.localeCompare(b.department)
      );
    } else {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        b.department.localeCompare(a.department)
      );
    }
  } else if (arg.sortBy === 'employeeName') {
    if (arg.sortOrder === 'asc') {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        a.employee.localeCompare(b.employee)
      );
    } else {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        b.employee.localeCompare(a.employee)
      );
    }
  } else if (arg.sortBy === 'complianceStatus') {
    if (arg.sortOrder === 'asc') {
      mostRecentRoundCleanData.sort(() => 0);
    } else {
      mostRecentRoundCleanData.sort(() => 0);
    }
  } else if (arg.sortBy === 'roundedBy') {
    if (arg.sortOrder === 'asc') {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        a.roundedBy.localeCompare(b.roundedBy)
      );
    } else {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        b.roundedBy.localeCompare(a.roundedBy)
      );
    }
  } else if (arg.sortBy === 'templateName') {
    if (arg.sortOrder === 'asc') {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        a.templateName.localeCompare(b.templateName)
      );
    } else {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        b.templateName.localeCompare(a.templateName)
      );
    }
  } else if (arg.sortBy === 'roundDate') {
    if (arg.sortOrder === 'asc') {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        a.date.localeCompare(b.date)
      );
    } else {
      mostRecentRoundCleanData.sort((a: any, b: any) =>
        b.date.localeCompare(a.date)
      );
    }
  }

  if (arg.isDownload) {
    return mostRecentRoundCleanData;
  }

  const extrapolatedAnswersRoundsData = mostRecentRoundCleanData.map(
    (round: any) => {
      // This is mapping out the answers
      const answers =
        round.answers?.map((ans: any) => {
          return {
            question: ans.question,
            answer:
              ans?.answer && ans.answer.length > 0 ? ans?.answer[0].value : ''
          };
        }) || [];

      const answersWithIndex = answers.map((obj: any, index: number) => {
        return {
          [`question${index + 1}`]: obj?.question?.replaceAll('"', ''),
          [`answer${index + 1}`]: obj?.answer
        };
      });
      const ansObject = answersWithIndex.reduce((acc: any, value: any) => {
        return {
          ...acc,
          ...value
        };
      }, {});

      return {
        ...round,
        type: round.templateName,
        ...ansObject
      };
    }
  );

  return extrapolatedAnswersRoundsData;
};

export const cleanEmployeeFrequencyReportDownload = (
  employeeFrequencyList: any,
  arg: any
): EmpFreqReport[] => {
  const collapsedFreqReportWithAnswersExtrapolated =
    employeeFrequencyList?.reduce((acc: EmpFreqReport[], round: any) => {
      const foundEmpIndex = acc.findIndex(
        r =>
          `${r?.employeeId}:${r?.department || 'Unknown'}` ===
          `${round?.employeeId}:${round?.employeeDept || 'Unknown'}`
      );
      const answers =
        round.answers?.map((ans: any) => {
          return {
            question: ans.question,
            answer:
              ans?.answer && ans.answer.length > 0 ? ans?.answer[0].value : ''
          };
        }) || [];
      const answersWithIndex = answers.map((obj: any, index: any) => {
        return {
          [`question${index + 1}`]: obj?.question?.replaceAll('"', ''),
          [`answer${index + 1}`]: obj?.answer
        };
      });
      const ansObject = answersWithIndex.reduce((ans: any, value: any) => {
        return {
          ...ans,
          ...value
        };
      }, {});
      const newRound = {
        department: round?.employeeDept || 'Unknown',
        employee:
          `${round?.employeeFirstName} ${round?.employeeLastName}` || '',
        employeeId: round?.employeeId || '',
        employeeFirstName: round?.employeeFirstName || '',
        employeeLastName: round?.employeeLastName || '',
        roundedBy: `${round?.userFirstName} ${round?.userLastName}`,
        createdUserId: round?.userId || '',
        date: round?.roundDate || '',
        type: round?.templateName || '',
        ...ansObject
      };
      if (foundEmpIndex === -1) {
        return [
          ...acc,
          {
            department: round?.employeeDept || 'Unknown',
            employeeId: round?.employeeId || '',
            employee:
              `${round?.employeeFirstName} ${round?.employeeLastName}` || '',
            employeeFirstName: round?.employeeFirstName || '',
            employeeLastName: round?.employeeLastName || '',
            count: 1,
            rounds: [newRound]
          }
        ];
      }
      acc[foundEmpIndex].count += 1;
      acc[foundEmpIndex].rounds.push(newRound);
      return [...acc];
    }, []) || [];

  // TODO: Front-end has to handle sorting until mobile no longer needs export endpoint
  if (arg.sortBy === 'employeeDept') {
    if (arg.sortOrder === 'asc') {
      collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
        a.department.localeCompare(b.department)
      );
    } else {
      collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
        b.department.localeCompare(a.department)
      );
    }
  } else if (arg.sortBy === 'employeeName') {
    if (arg.sortOrder === 'asc') {
      collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
        a.employee.localeCompare(b.employee)
      );
    } else {
      collapsedFreqReportWithAnswersExtrapolated.sort((a: any, b: any) =>
        b.employee.localeCompare(a.employee)
      );
    }
  } else if (arg.sortBy === 'total') {
    if (arg.sortOrder === 'asc') {
      collapsedFreqReportWithAnswersExtrapolated.sort(
        (a: any, b: any) => a.count - b.count
      );
    } else {
      collapsedFreqReportWithAnswersExtrapolated.sort(
        (a: any, b: any) => b.count - a.count
      );
    }
  }

  return collapsedFreqReportWithAnswersExtrapolated;
};
